import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { get } from 'lodash';
import { ModalComponent, SpinnerComponent } from 'remarketing-ui-components-lib';
import {
  BidItem,
  BidListService,
  BuyErrors,
  PurchaseActionService,
  BuyNowDTO,
  AuctionItem,
  LaunchDarklyService,
  FinanceSource,
  UserService
} from 'remarketing-angular-library';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { BidModalItem } from '../../models/bid-modal-item.model';
import { AnalyticsService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { Store } from '@ngxs/store';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { VehicleChangedWarningComponent } from '../offer-modals/vehicle-changed-warning/vehicle-changed-warning.component';
import { RouterLink } from '@angular/router';
import { ActionSubmittingComponent } from 'src/shared/ui/comment/modal-actions/action-submitting/action-submitting.component';
import { ActionSubmittedComponent } from 'src/shared/ui/comment/modal-actions/action-submitted/action-submitted.component';
import { ModalCloseComponent } from 'src/shared/ui/comment/modal-actions/modal-close/modal-close.component';
import { AuctionItemFee } from 'remarketing-angular-library/interfaces/auction-item';
import { DripFeeSource, DripPricingService } from '../../../shared/services/drip-pricing/public-api';
import { FeeToolTipService } from '../../../shared/services/feetooltip/feetooltip.service';
import {
  ModalPriceBreakdownComponent,
  modalTotalDueOf,
  showsTransportationFee
} from '../../../shared/components/modal-price-breakdown/modal-price-breakdown.component';
import {
  ModalPriceBreakdownPattern,
  ModalPriceBreakdownRow,
  ModalPriceBreakdownTranslations
} from '../../../shared/components/modal-price-breakdown/modal-price-breakdown.types';

@Component({
    selector: 'app-buy-now-modal',
    templateUrl: './buy-now-modal.component.html',
    styleUrls: ['./buy-now-modal.component.scss'],
    imports: [
        TranslocoDirective,
        SpinnerComponent,
        VehicleChangedWarningComponent,
        RouterLink,
        ActionSubmittingComponent,
        ActionSubmittedComponent,
        ModalComponent,
        ModalCloseComponent,
        ModalPriceBreakdownComponent,
        CommonModule
    ]
})
export class BuyNowModalComponent implements OnInit, OnDestroy {

  get title() {
    return this.modalItem.title;
  }

  /**
   * The vehicle name for the all-in branch's identification block, WITHOUT the ' - VIN ' tail that
   * `title` above carries. The flag-off prose puts the VIN inline directly after the name, so `title`
   * ends in that label; the comp puts the VIN on its own line with its own common.textVin label, and
   * reusing `title` there would read '2017 Toyota Camry XLE - VIN' followed by 'VIN 4T1BF...'.
   *
   * Delegates to BidModalItem.titleSimple rather than trimming `title` here: that getter is the one
   * the bid modal's identical block already uses, so the two modals cannot drift on how a vehicle is
   * named.
   */
  get titleSimple() {
    return this.modalItem.titleSimple;
  }

  get showSellingDisclosure(): boolean {
    return this._launchDarklyService.getFeatureFlag('disclosuresLinkEnabled', false);
  }

  get vin() {
    return this.modalItem?.itemDetail?.vin;
  }

  constructor(
    private bidListService: BidListService,
    private purchaseActionService: PurchaseActionService,
    private readonly analyticsService: AnalyticsService,
    private readonly store: Store,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly userService: UserService,
    private readonly translocoService: TranslocoService,
    private readonly dripPricingService: DripPricingService,
    private readonly feeToolTipService: FeeToolTipService
  ) {}

  @ViewChild(ModalComponent) modal: ModalComponent;
  @Input() auctionItem: AuctionItem;
  @Input() tenantName: string;
  @Input() modalId = 'Buy-Now';
  @Input() modalItem: BidModalItem;
  @Input() buyNowAmount: number;
  @Input() financeSource: FinanceSource;
  /**
   * The host page's fee payload, and the all-in breakdown's preferred fee source.
   *
   * Bound by the vehicle detail pages, which load it with the vehicle and keep it current. Left unset
   * by the bid list, which launches this modal through ModalLauncherService and has no equivalent; see
   * dripFeeSource for how the two paths resolve.
   */
  @Input() auctionItemFee: AuctionItemFee;
  errorMessage: string;
  bidItem: BidItem;
  isLoading = true;
  showWarning = false;
  private readonly destroyed$ = new Subject();
  sellingDisclosureUrl: string;

  @Output() bought = new EventEmitter();

  buyState: 'notBought' | 'buying' | 'bought' = 'notBought';

  buyNowModalTranslations = {
    textBuyNow: '',
    textImportant: '',
    textBySelecting: '',
    textYouAgree: '',
    textAtPrice: '',
    /**
     * Flag-off only. With the flag on this sentence is not reworded but replaced outright, so there is
     * no all-in variant of it -- M4.2's contentSelectingBuyNowAllIn is gone. That key existed because
     * the closing clause "plus any applicable charges or fees" is false once the fees are inside the
     * quoted figure; the comp answers the same problem by supplying a new sentence, which also drops
     * the per-tenant overrides of this one ("...which may include optional Transportation Fees" in
     * ehi/ehi_ca, "charges, fees, or taxes" in en-CA/fr-CA) that all-in pricing contradicts.
     */
    contentSelectingBuyNow: '',
    textTransportationService: ''
  };

  ngOnInit() {
    this.store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => {
        this.sellingDisclosureUrl = get(configuration, 'sellingDisclosureUrl', null);
      });
    this.getTranslations();
  }

  formatCurrency(currency) {
    return this.currencyFormatService.formatCurrency(currency, false).replaceAll('-', '');
  }

  /**
   * The all-in figure for the disclosure sentence, formatted exactly as the breakdown beneath it
   * formats the same number, so it is never written two ways inside one modal. A buyer reading
   * "$59,300" in the sentence and "$59,300.00" in the block below has to work out whether those are
   * the same figure, and on a binding-purchase screen that is not a cosmetic problem.
   *
   * withCents went from true to false in M4.13 to match the comp, which quotes whole dollars in both
   * places. The pairing is what matters rather than either value: this argument and the template's
   * [showCents] must move together, and nothing but the comp decides which way.
   *
   * Still distinct from formatCurrency() above despite now agreeing on cents. That one also strips
   * '-' characters, which turns a negative into a positive rather than signalling one, and it renders
   * the unchanged flag-off prose -- so it is left exactly as it is.
   */
  formatAllInCurrency(currency: number): string {
    return this.currencyFormatService.formatCurrency(currency, false);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /**
   * bidItem and showWarning are CLEARED before the fetch, not only overwritten after a successful one.
   *
   * The subscribe below assigns inside `if (bidListItem)`, and the pipe catchErrors a failure to
   * undefined, so a failed lookup used to leave whatever the previous open had put there. On
   * auction-details this component is a single static instance for the route, and Angular reuses the
   * route component when the user navigates from one VDP to another, so "the previous open" can be a
   * DIFFERENT VEHICLE. Vehicle A opens and fetches fine, the user navigates to vehicle B, B's lookup
   * fails -- and bidItem, one of the two drip fee sources, still holds A's fees. showDripBreakdown is
   * satisfied (a payload did arrive, just not this one), so the modal quotes B's price plus A's fees as
   * an all-in total and binds the buyer to it in the disclosure sentence. That is the precise outcome
   * showDripBreakdown's docblock says a failed fetch must avoid by falling back to the flag-off prose,
   * and without this line the gate cannot tell the two apart.
   *
   * Within one vehicle it matters too: a retry after a transient failure would otherwise re-assert fees
   * the component just failed to confirm.
   *
   * Clearing bidItem no longer means a failed lookup always falls back to the prose, because
   * auctionItemFee can still supply fees -- and that is the right answer rather than a hole in this
   * guard: it is the host page's own payload for the vehicle currently on screen, not a leftover from
   * the previous one. What this line still does, and still has to do, is stop a STALE payload standing
   * in for a missing one. See dripFeeSource for the precedence.
   *
   * showWarning is the same staleness in a different field -- A's buyerConfirmationRequired surviving
   * onto B -- so it is reset in the same breath rather than left as the one stale thing here.
   */
  show() {
    this.isLoading = true;
    this.errorMessage = null;
    this.bidItem = undefined;
    this.showWarning = false;
    this.bidListService
      .getBidListItem(this.modalItem.auctionItemUuid)
      .pipe(
        takeUntil(this.destroyed$),
        catchError(() => of(undefined))
      )
      .subscribe((bidListItem: BidItem) => {
        if (bidListItem) {
          this.showWarning = bidListItem.buyerConfirmationRequired;
          this.bidItem = bidListItem;
        }
        this.isLoading = false;
        this.changeDetector.markForCheck();
        setTimeout(() => {
          const heading = document.getElementById(this.modalId + '-title');
          if (heading) {
            heading.focus();
          }
        }, 100);
      });
    this.modal.show();
    setTimeout(() => {
      //timeout is necessary since we are working outside of the Angular ngZone
      this.changeDetector.markForCheck();
    }, 2000);
  }

  isFinanceSourceFeeIncluded() {
    return this.financeSource?.feeAmount != null && this.financeSource?.feeAmount != 0;
  }

  // ---------------------------------------------------------------------------
  // All-in pricing (drip pricing, Canada's Competition Act) -- M4.2
  //
  // An advertised price the buyer cannot actually pay is deceptive marketing. Behind
  // `buy-allInPricingEnabled` this modal stops quoting the bare vehicle price followed by "plus any
  // applicable charges or fees", and instead quotes the all-in price (vehicle + required fees) and
  // decomposes it in the shared M4.1 breakdown, with optional transportation as its own row.
  //
  // `buyNowAmount` is the RAW vehicle price at all three call sites --
  // auction-details.component.html, auction-details-refactor.component.html, and
  // BidlistInventoryItemComponent.openBuyNowModals, which passes `bidItem.buyNowAmount` and NOT its
  // own all-in `dripBuyNowAmount` getter. That is load-bearing: handing an already-all-in amount to
  // the breakdown double-counts every required fee and yields a plausible-looking wrong number under
  // a compliance label rather than a crash. If a future call site binds a displayed figure here, this
  // entire block becomes wrong.
  // ---------------------------------------------------------------------------

  /** Pattern A: one bold all-in figure, decomposed beneath. Buy Now has no amount input. */
  readonly decomposedPattern = ModalPriceBreakdownPattern.DECOMPOSED;

  /**
   * The host page's fees where there are any, and the fetched bid list item's otherwise.
   *
   * The two launch paths carry different payloads, so the modal takes whichever its launcher has
   * rather than deciding for itself. A detail page binds auctionItemFee; the bid list opens this
   * through ModalLauncherService and leaves it unset, so that path resolves to bidItem?.buyFee and
   * behaves exactly as it did before this input existed.
   *
   * auctionItemFee is NOT merely a fallback for the bid list fetch -- it takes precedence over it,
   * including for a vehicle that IS on the buyer's list. Two reasons. It is the payload the page
   * around this modal is already pricing from, so preferring it is what stops the modal and the page
   * behind it quoting different all-in totals for one vehicle. And it stays current: the detail page
   * refreshes it from the fee-calculation and websocket updates, while bidItem is a single response
   * fetched once when the modal opened and never revised.
   *
   * Reading bidItem?.buyFee alone -- which is what this did until the fee source was widened -- tied
   * the breakdown to bid list membership. getBidListItem is a GET on the BID LIST resource, so for a
   * vehicle the buyer has not listed it returns nothing, show()'s catchError swallows that to
   * undefined, and hasFeePayload fails on an absent source. The modal then fell back to the pre-drip
   * design while the page behind it priced the same vehicle all-in. Membership is not a drip gate; the
   * flag and the presence of fees are the only conditions (see showDripBreakdown).
   *
   * Reported against a vehicle whose auction had not yet opened, which is a correlation rather than
   * the cause: nothing in this getter, in showDripBreakdown or in hasFeePayload reads an auction time
   * or status. Such a vehicle is simply one typically reached from Buy Search or upcoming inventory
   * and not yet on the buyer's list. The same widening is applied to bid-modal and bid-modal-refactor
   * in this change.
   */
  private get dripFeeSource(): DripFeeSource {
    return this.auctionItemFee?.buyFee ?? this.bidItem?.buyFee;
  }

  get allInBuyNowPrice(): number | undefined {
    return this.dripPricingService.allInPriceOf(this.buyNowAmount, this.dripFeeSource);
  }

  /**
   * Whether this modal renders all-in content at all. Three conditions, each load-bearing:
   *
   * - the flag, so the kill switch works here as it does everywhere else;
   * - a fee payload, because it IS the fee source. It arrives either from the host page's
   *   auctionItemFee binding or, failing that, from the HTTP call in show() that catchErrors to
   *   undefined -- see dripFeeSource. Without one requiredFeesOf returns [] and allInPriceOf returns
   *   the bare vehicle price -- a DEFINED number, so the finite check below cannot catch it.
   *   Presenting the vehicle price as an all-in total because a fetch failed is the worst outcome
   *   available here, so having NEITHER source falls back to today's prose instead;
   * - a finite all-in price, which also covers `buyNowAmount` arriving as the string 'N/A' for the
   *   view-only role (both auction-details templates bind that literal).
   *
   * The fee payload is tested with hasFeePayload on the fee source rather than `!!this.bidItem`.
   * `bidItem` is the response the fees arrive IN, not the fees: a bidItem that came back without a
   * buyFee is truthy and carries nothing, which is precisely the DEFINED-but-bare total the bullet
   * above says the finite check cannot catch. The same goes for a buyFee of `{}` or `{ fees: null }`.
   * hasFeePayload asks whether a fee COLLECTION arrived.
   *
   * Not requiredFeesOf(...).length either, and the service docblock says so explicitly: a payload that
   * arrived carrying no required fee must still render, with the all-in total legitimately equal to the
   * bare price. Gating on the row count would withhold the breakdown from a correct zero-fee answer.
   *
   * There is deliberately no isSharedList/isPurchasedVehicle gate as on the card: Buy Now is not
   * offered on a shared list or for an already-purchased vehicle, so this modal cannot open in
   * either state.
   */
  get showDripBreakdown(): boolean {
    return this.dripPricingService.isDripPricingEnabled
      && this.dripPricingService.hasFeePayload(this.dripFeeSource)
      && this.allInBuyNowPrice !== undefined;
  }

  get dripTransportationFee(): number | undefined {
    return this.dripPricingService.transportationFeeOf(this.dripFeeSource);
  }

  /**
   * Governs the "transportation is optional" paragraph. Calls the same exported helper the breakdown
   * row itself uses, so the paragraph and the row appear and disappear together: telling the buyer
   * they can avoid a fee that is not quoted, or quoting one without saying it is avoidable, are both
   * defects this one shared predicate prevents. Do not re-derive it as `fee > 0`.
   *
   * Kept although the comp (Figma 10360:9217) shows no such paragraph: the all-in modal quotes a
   * binding total that INCLUDES transportation and renders a TRANSPORTATION FEE row, so the buyer has
   * to be told that part is avoidable. The comp omitting the sentence does not make the disclosure
   * optional. This getter checks only the fee; the drip flag gates it from the template, where the
   * paragraph sits inside the `@if (showDripBreakdown)` branch.
   */
  get showTransportationDisclosure(): boolean {
    return showsTransportationFee(this.dripTransportationFee);
  }

  /**
   * The breakdown's own bottom line: vehicle + required fees + transportation, where transportation
   * exists. Not a second calculation of it -- the same exported helper the breakdown itself uses, so
   * the sentence and the total row cannot disagree by construction.
   *
   * Safe to read unguarded inside the flag-on branch: showDripBreakdown already requires a finite
   * allInBuyNowPrice, which requires a finite buyNowAmount, so this is only undefined where the
   * branch does not render. The bid modal needs a placeholder for the undefined case because there
   * the buyer types the amount; here the price is the listing's own.
   */
  get modalTotalDue(): number | undefined {
    return modalTotalDueOf(this.buyNowAmount, this.dripFeeRows, this.dripTransportationFee);
  }

  /**
   * The figure quoted in the binding sentence.
   *
   * M4.12 changed this from the all-in price to the transportation-INCLUSIVE total, and the comps are
   * why. M4.2's reasoning was that transportation is separately elected, so binding the buyer to a
   * transportation-inclusive figure overstates the obligation. The matched comp pair disproves it as
   * a reading of the design: 10360:9184 and 10360:9217 are the same modal differing only in whether
   * a transportation fee exists, and the quoted figure is $59,650 in the first and $59,300 in the
   * second -- it tracks the TOTAL PURCHASE PRICE on screen, not the all-in subtotal.
   *
   * The obligation objection is answered by the sentence that follows rather than by the figure: the
   * buyer is told transportation is optional and how to remove it, and removing it re-renders both
   * the total and this figure without it. Quoting the all-in price instead would put a number in the
   * binding sentence that appears nowhere in the itemisation directly above it, which is the defect
   * this epic exists to remove. Same choice M4.10 made for View Offer and M4.11 for Bid.
   */
  get quotedPriceDisplay(): string {
    return this.showDripBreakdown
      ? this.formatAllInCurrency(this.modalTotalDue)
      : this.formatCurrency(this.buyNowAmount);
  }

  /**
   * The fee rows, cached by content.
   *
   * The cache is not an optimisation. translatedRequiredFeesOf maps a fresh array every call, and a
   * template binding is re-evaluated on every change-detection pass, so returning it directly would
   * (a) hand the OnPush child a new identity every pass, defeating its change detection, and (b)
   * trip NG0100 in development builds, where Angular re-checks bindings and compares by reference.
   * Keying on the serialised content returns a stable reference while still picking up a genuine
   * change -- a fee amount moving, or labels arriving once Transloco resolves.
   */
  get dripFeeRows(): ModalPriceBreakdownRow[] {
    const rows = this.feeToolTipService.translatedRequiredFeesOf(this.dripFeeSource);
    const key = JSON.stringify(rows);
    if (key !== this.dripFeeRowsKey) {
      this.dripFeeRowsKey = key;
      this.dripFeeRowsCache = rows;
    }
    return this.dripFeeRowsCache;
  }

  /** Cached by content for the same reason as dripFeeRows. */
  get dripBreakdownTranslations(): ModalPriceBreakdownTranslations {
    const drip = this.feeToolTipService.dripTranslations;
    const translations: ModalPriceBreakdownTranslations = {
      labelBaseAmount: drip.labelBuyNowPrice,
      labelVehiclePrice: drip.labelVehiclePrice,
      labelTransportationFee: this.feeToolTipService.feeTranslations.TRANSPORTATION_FEE,
      // labelModalTotalDue, never labelTotalPurchasePrice: this total INCLUDES transportation while
      // the list-surface total excludes it. See the collision note on FeeToolTipService.dripTranslations.
      labelTotalDue: drip.labelModalTotalDue,
      textTotalPlaceholder: drip.textTotalPlaceholder,
      ariaLabelPriceComponents: drip.ariaLabelPriceComponents
    };
    const key = JSON.stringify(translations);
    if (key !== this.dripBreakdownTranslationsKey) {
      this.dripBreakdownTranslationsKey = key;
      this.dripBreakdownTranslationsCache = translations;
    }
    return this.dripBreakdownTranslationsCache;
  }

  private dripFeeRowsKey: string;
  private dripFeeRowsCache: ModalPriceBreakdownRow[];
  private dripBreakdownTranslationsKey: string;
  private dripBreakdownTranslationsCache: ModalPriceBreakdownTranslations;

  buyNow() {
    this.buyState = 'buying';

    if (this.userService.isViewOnlyRole()) {
      this.errorMessage = 'W-REMPLT-AIS-VOU';
      this.buyState = 'notBought';
      return;
    }

    const { auctionItemUuid, buyerOeUuid, financeSourceUuid, localDelivery, financeSourceAch } = this.modalItem;
    const amount = this.buyNowAmount;
    const payload: BuyNowDTO = {
      auctionItemUuid,
      buyerOeUuid,
      financeSourceUuid,
      financeSourceAch,
      localDelivery,
      amount
    };

    this.purchaseActionService
      .buyNow(payload)
      .pipe(take(1))
      .subscribe(
        () => {
          this.buyState = 'bought';
          this.analyticsService.fireBuyActionEvent('buyNowBuyAction', 1, [this.modalItem.auctionItemUuid]);
          this.changeDetector.markForCheck();
        },
        error => {
          this.errorMessage = error.error.errors[0].code;
          this.buyState = 'notBought';
          this.analyticsService.track(
            'customError',
            this.errorMessage,
            null,
            this.analyticsService.createBidEventAnalyticsPayload(payload, 'buyNow', this.errorMessage)
          );
          this.changeDetector.markForCheck();
        }
      );
  }

  private getError(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      return BuyErrors.MSG_BUY_002;
    }
    const buyNowError = get(error, 'error.errors[0]', { code: 'No Error Found' });
    return BuyErrors.fromBuyErrors(buyNowError.code);
  }

  close() {
    if (this.buyState === 'bought') {
      this.bought.emit();
    }
    this.modal.close();
  }

  getTranslations() {
    const keyParams = {
      'vehicles.buyNowModal': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.buyNowModalTranslations.textBuyNow = translate[0].textBuyNow;
        this.buyNowModalTranslations.textImportant = translate[0].textImportant;
        this.buyNowModalTranslations.textBySelecting = translate[0].textBySelecting;
        this.buyNowModalTranslations.textYouAgree = translate[0].textYouAgree;
        this.buyNowModalTranslations.textAtPrice = translate[0].textAtPrice;
        this.buyNowModalTranslations.contentSelectingBuyNow = translate[0].contentSelectingBuyNow;
        this.buyNowModalTranslations.textTransportationService = translate[0].textTransportationService;
      });
  }
}
